import type { NavigationBadges } from './types/navigation-badges';
export type { NavigationBadges } from './types/navigation-badges';

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export const useNavigationBadges = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['navigation-badges', user?.id],
    queryFn: async (): Promise<NavigationBadges> => {
      if (!user?.id) {
        return { alerts: 0, maintenance: 0, messages: 0, properties: 0 };
      }

      // Fetch counts in parallel
      const [alertsResult, maintenanceResult, messagesResult] = await Promise.all([
        // Active high/critical alerts
        supabase
          .from('alerts')
          .select('id', { count: 'exact', head: true })
          .eq('is_active', true)
          .in('severity', ['high', 'critical']),
        
        // Pending/in-progress maintenance requests
        supabase
          .from('maintenance_requests')
          .select('id', { count: 'exact', head: true })
          .in('status', ['pending', 'in_progress']),
        
        // Unread communications (simplified - checking recent unread)
        supabase
          .from('communication_recipients')
          .select('id', { count: 'exact', head: true })
          .eq('recipient_id', user.id)
          .is('read_at', null)
      ]);

      return {
        alerts: alertsResult.count || 0,
        maintenance: maintenanceResult.count || 0,
        messages: messagesResult.count || 0,
        properties: 0, // Properties don't typically have badge counts
      };
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 10000, // Consider stale after 10 seconds
  });
};
